/**
 * Helpers shared by the media-control blocks (trace/playMedia, pauseMedia,
 * stopMedia, seekMedia, setMediaVolume). All five resolve the same way:
 *
 *   1. Resolve target path → SceneObject (defaults to context object).
 *   2. Find the first behavior on that object that implements Playable.
 *
 * Resolution order favours media behaviors (Video, AudioClip) over generic
 * Playables (Animation, Pages) so that a target with both still routes media
 * controls to the media behavior. Within a class, behavior insertion order
 * wins (same as `getBehavior(Class)`).
 */

import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { SceneObject } from '../../../core/SceneObject';
import { isPlayable, hasPlayableSource, type Playable } from '../../playable';
import { resolveTarget } from './traceContext';

/**
 * Resolve a target path to its first Playable behavior, or null if either
 * the target can't be resolved or no behavior implements Playable.
 *
 * Behaviors that implement `PlayableSource` (Video, AudioClip — media backed
 * by an HTMLMediaElement) take precedence over generic Playables (Animation,
 * Pages) when both are attached. This matches the user's mental model:
 * "play media on this object" should drive the media component, not the
 * animation that happens to be alongside it.
 */
export function resolvePlayable(
    context: FlowGraphContext,
    targetPath: string,
): { target: SceneObject; playable: Playable } | null {
    const target = resolveTarget(context, targetPath);
    if (!target) return null;
    const playable = pickPlayable(target);
    if (!playable) return null;
    return { target, playable };
}

function pickPlayable(target: SceneObject): Playable | null {
    let fallback: Playable | null = null;
    for (const behavior of target.getBehaviors()) {
        if (!isPlayable(behavior)) continue;
        if (hasPlayableSource(behavior)) return behavior;
        if (!fallback) fallback = behavior;
    }
    return fallback;
}
